import { PropsWithChildren } from 'react';

import HostingSSO from '../SSO/components/HostingSSO';
import ResellerWhmSSO from '../SSO/components/ResellerWhmSSO';
import SitelockSSO from '../SSO/components/SitelockSSO';
import SiteSSO from '../SSO/components/SiteSSO';
import SpamExpertsSSO from '../SSO/components/SpamExpertsSSO';
import SystemConsoleSSO from '../SSO/components/SystemConsoleSSO';
import WebmailSSO from '../SSO/components/WebmailSSO';
import WhmSSO from '../SSO/components/WhmSSO';
import {
  HOSTING,
  PLESK,
  RESELLER,
  SITE,
  SITELOCK,
  SPAMEXPERTS,
  VPS,
  WEBMAIL,
  WINDOWS,
} from './utils/constants';
import { SsoLinkPropOptions } from './utils/types';

const SSOLink = ({
  children = undefined,
  type = SITE,
  queryParams = {},
  ssoQueryParams = '',
  fluid = false,
  variant = 'outlined',
  userId = undefined,
  addonId = undefined,
  onAction = undefined,
  onPrepareMFEContext = undefined,
  id = undefined,
  disabled = undefined,
  siteType = 'wordpress',
  hostingId = undefined,
  advHostingQuickLink = false,
  dataTestId = 'sso-link',
  showIcon = false,
  ...props
}: PropsWithChildren<SsoLinkPropOptions>) => {
  // Delegate to the appropriate focused SSO component based on type
  const commonProps = {
    queryParams,
    ssoQueryParams,
    fluid,
    variant,
    disabled,
    onAction,
    onPrepareMFEContext,
    dataTestId,
    showIcon,
    advHostingQuickLink,
    id,
    ...props,
  };

  switch (type) {
    case SITE:
      return (
        <SiteSSO
          siteId={id}
          userId={userId}
          addonId={addonId}
          siteType={siteType}
          {...commonProps}
        >
          {children}
        </SiteSSO>
      );

    case HOSTING:
    case PLESK:
    case WINDOWS:
      return (
        <HostingSSO hostingId={hostingId} {...commonProps}>
          {children}
        </HostingSSO>
      );

    case SPAMEXPERTS:
      if (!addonId) {
        return null;
      }
      return (
        <SpamExpertsSSO addonId={addonId} {...commonProps}>
          {children}
        </SpamExpertsSSO>
      );

    case VPS:
      // VPS type handles both WHM and System Console based on app parameter
      if (queryParams?.app === 'whm') {
        return (
          <WhmSSO hostingId={hostingId} {...commonProps}>
            {children}
          </WhmSSO>
        );
      }
      // Default to SystemConsole for VPS
      return (
        <SystemConsoleSSO hostingId={hostingId} {...commonProps}>
          {children}
        </SystemConsoleSSO>
      );

    case SITELOCK:
      if (!addonId) {
        return null;
      }
      return (
        <SitelockSSO addonId={addonId} {...commonProps}>
          {children}
        </SitelockSSO>
      );

    case RESELLER:
      // Reseller type handles both Reseller WHM and regular hosting based on app parameter
      if (queryParams?.app === 'resellerwhm') {
        return (
          <ResellerWhmSSO hostingId={hostingId}>{children}</ResellerWhmSSO>
        );
      }
      // Default to regular hosting SSO for reseller
      return <HostingSSO hostingId={hostingId}>{children}</HostingSSO>;

    case WEBMAIL:
      return (
        <WebmailSSO hostingId={hostingId} {...commonProps}>
          {children}
        </WebmailSSO>
      );

    default:
      // Fallback to HostingSSO for unknown types
      return (
        <HostingSSO hostingId={hostingId} {...commonProps}>
          {children}
        </HostingSSO>
      );
  }
};

export default SSOLink;
